import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "../../styles/Select.module.css";
import { Arrow } from "../../../public/icons";
import { ActionButton, ArrowWrapper } from "../../styles/Button.styled";

export interface OptionAutocompleteInputCustom {
  id: string | number;
  title: string;
  alias?: string;
  isDisabled?: boolean;
}

interface AutocompletePropsBase {
  label: string;
  options: OptionAutocompleteInputCustom[];
  disabled?: boolean;
  placeholder?: string;
  width?: string;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
}

interface SingleProps extends AutocompletePropsBase {
  multiple?: false;
  value?: string | number | null;
  onChange: (option: OptionAutocompleteInputCustom | null) => void;
}

interface MultipleProps extends AutocompletePropsBase {
  multiple: true;
  value?: (string | number)[];
  onChange: (options: OptionAutocompleteInputCustom[]) => void;
}

type AutocompleteProps = SingleProps | MultipleProps;

function highlightMatch(title: string, search: string): React.ReactNode {
  if (!search) return title;
  const index = title.toLowerCase().indexOf(search.toLowerCase());
  if (index === -1) return title;
  return (
    <>
      {title.slice(0, index)}
      <strong style={{ color: "var(--blue)" }}>
        {title.slice(index, index + search.length)}
      </strong>
      {title.slice(index + search.length)}
    </>
  );
}

function filterOptions(
  options: OptionAutocompleteInputCustom[],
  query: string,
): OptionAutocompleteInputCustom[] {
  if (!query) return options;
  const lower = query.toLowerCase();
  return options.filter((opt) => opt.title.toLowerCase().includes(lower));
}

export const AutocompleteInputCustom: React.FC<AutocompleteProps> = (props) => {
  const {
    label,
    options = [],
    disabled,
    placeholder = "Оберіть або введіть",
    width,
    style,
    labelStyle,
    multiple = false,
    value,
    onChange,
  } = props;

  const activeOptions = useMemo(
    () =>
      options
        .filter((opt) => !opt.isDisabled || opt.id === value)
        .sort((a, b) => a.title.localeCompare(b.title)),
    [options, value],
  );
  const allOptions = useMemo(
    () =>
      options
        .filter((opt) => !opt.isDisabled)
        .sort((a, b) => a.title.localeCompare(b.title)),
    [options],
  );
  const selectedOption = useMemo(
    () =>
      !multiple
        ? (activeOptions.find((opt) => opt.id === value) ?? null)
        : null,
    [activeOptions, multiple, value],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(selectedOption?.title ?? "");
  const [filtered, setFiltered] = useState(activeOptions);

  const initialMulti: OptionAutocompleteInputCustom[] = (() => {
    if (!multiple || !value) return [];
    const ids = Array.isArray(value) ? value : [value];
    return activeOptions.filter((opt) => ids.includes(opt.id));
  })();
  const [selectedMulti, setSelectedMulti] = useState(initialMulti);
  const [dropdownDirection, setDropdownDirection] = useState<"down" | "up">(
    "down",
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const updateDropdownDirection = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 250;

    setDropdownDirection(
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? "up" : "down",
    );
  }, []);

  const handleFocus = useCallback(() => {
    updateDropdownDirection();
    setIsOpen(true);
    setFiltered(allOptions);
  }, [allOptions, updateDropdownDirection]);

  useEffect(() => {
    if (multiple) return;
    setInputValue(value != null ? (selectedOption?.title ?? "") : "");
  }, [value, multiple, selectedOption]);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
        setFiltered(allOptions);
        if (!multiple) {
          setInputValue(selectedOption?.title ?? "");
        }
      }
    },
    [allOptions, multiple, selectedOption],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);
      setIsOpen(true);
      setFiltered(filterOptions(allOptions, val));

      if (val === "" && !multiple) {
        (onChange as SingleProps["onChange"])(null);
      }
    },
    [allOptions, multiple, onChange],
  );

  const handleSelect = useCallback(
    (opt: OptionAutocompleteInputCustom) => {
      if (!multiple) {
        (onChange as SingleProps["onChange"])(opt);
        setInputValue(opt.title);
        setIsOpen(false);
        setFiltered(allOptions);
        return;
      }

      const isAlreadySelected = selectedMulti.some((s) => s.id === opt.id);
      const newSelected = isAlreadySelected
        ? selectedMulti.filter((s) => s.id !== opt.id)
        : [...selectedMulti, opt];

      setSelectedMulti(newSelected);
      (onChange as MultipleProps["onChange"])(newSelected);
      setInputValue("");
      inputRef.current?.focus();
    },
    [allOptions, multiple, onChange, selectedMulti],
  );

  const handleRemoveTag = useCallback(
    (id: string | number, e: React.MouseEvent) => {
      e.stopPropagation();
      const newSelected = selectedMulti.filter((s) => s.id !== id);
      setSelectedMulti(newSelected);
      (onChange as MultipleProps["onChange"])(newSelected);
    },
    [onChange, selectedMulti],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (multiple) {
        setSelectedMulti([]);
        (onChange as MultipleProps["onChange"])([]);
      } else {
        (onChange as SingleProps["onChange"])(null);
      }
      setInputValue("");
      setFiltered(allOptions);
      inputRef.current?.focus();
    },
    [allOptions, multiple, onChange],
  );

  const hasValue = multiple ? selectedMulti.length > 0 : inputValue.length > 0;

  return (
    <div
      className={styles.formControl}
      style={{ width, ...style }}
      ref={wrapperRef}
    >
      <div className={styles.selectWrapper}>
        {multiple && selectedMulti.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              padding: "6px 8px 0",
            }}
          >
            {selectedMulti.map((opt) => (
              <span
                key={opt.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "2px 8px",
                  backgroundColor: "rgba(24, 96, 217, 0.1)",
                  borderRadius: "20px",
                  fontSize: "12px",
                  color: "var(--blue)",
                }}
              >
                {opt.title}
                <button
                  type="button"
                  onClick={(e) => handleRemoveTag(opt.id, e)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    color: "var(--blue)",
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <input
          ref={inputRef}
          type="text"
          className={styles.select}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          maxLength={40}
          style={{paddingRight: "36px"}}
          disabled={disabled}
          placeholder={multiple && selectedMulti.length > 0 ? "" : placeholder}
        />

        <label className={styles.label} style={labelStyle}>
          {label}
        </label>

        {!disabled && (
          <ActionButton
            type="button"
            $hasOffset={selectedMulti.length > 0}
            onClick={hasValue ? handleClear : () => setIsOpen(true)}
            aria-label={hasValue ? "Очистити" : "Відкрити список"}
          >
            {hasValue ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <ArrowWrapper $isOpen={isOpen}>
                <Arrow color="var(--disabled-c)" />
              </ArrowWrapper>
            )}
          </ActionButton>
        )}

        {isOpen && (
          <ul
            className={styles.dropdown}
            style={
              dropdownDirection === "up"
                ? {
                    bottom: "100%",
                    top: "auto",
                    marginBottom: "2px",
                    marginTop: 0,
                  }
                : { top: "100%", bottom: "auto", marginTop: "2px" }
            }
          >
            {filtered.length > 0 ? (
              filtered.map((opt) => {
                const isSelected = multiple
                  ? selectedMulti.some((s) => s.id === opt.id)
                  : opt.id === value;

                return (
                  <li key={opt.id}>
                    <button
                      type="button"
                      className={`${styles.account_btn} ${isSelected ? styles.selected : ""}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(opt)}
                    >
                      {multiple && (
                        <span style={{ marginRight: "8px" }}>
                          {isSelected ? "☑" : "☐"}
                        </span>
                      )}
                      {highlightMatch(opt.title, inputValue)}
                    </button>
                  </li>
                );
              })
            ) : (
              <li
                style={{
                  padding: "12px 15px",
                  color: "var(--disabled-c)",
                  fontSize: "14px",
                }}
              >
                Нічого не знайдено
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
